import * as THREE from 'three';
import { Spin } from '../animated';
import { MODERN } from '../materials';
import { Beam, type ModelProps, Pole } from '../parts';

const { CONCRETE, DARK_CONCRETE, GUNMETAL, TRACK, STEEL, WHITE, HAZARD, DISH } = MODERN;

const G = {
  pad: new THREE.CylinderGeometry(0.6, 0.62, 0.04, 6),
  collar: new THREE.CylinderGeometry(0.2, 0.22, 0.05, 12),
  shaft: new THREE.CylinderGeometry(0.16, 0.16, 0.052, 12),
  missileBody: new THREE.CylinderGeometry(0.09, 0.09, 0.12, 10),
  missileNose: new THREE.ConeGeometry(0.09, 0.22, 10),
  door: new THREE.BoxGeometry(0.2, 0.03, 0.4),
  doorStripe: new THREE.BoxGeometry(0.03, 0.005, 0.4),
  bunker: new THREE.BoxGeometry(0.22, 0.1, 0.15),
  bunkerDoor: new THREE.BoxGeometry(0.06, 0.07, 0.01),
  dish: new THREE.SphereGeometry(0.11, 12, 4, 0, Math.PI * 2, 0, 0.9).rotateX(-Math.PI / 2),
  lamp: new THREE.BoxGeometry(0.025, 0.025, 0.025),
};

const SHAFT_Z = -0.04;
// Doors hinge at the shaft's sides and stand open past vertical, leaning out.
const DOOR_HINGE = 0.21;
const DOOR_OPEN = 1.9;
const RADAR: [number, number, number] = [0.38, 0, -0.26];
const MAST = 0.36;
const RADAR_SPEED = 0.8; // radians per second

/**
 * A missile silo: a concrete pad with the shaft's blast doors standing open,
 * a white missile with a team-coloured nose rising from it, a low control
 * bunker with a lit lamp at the front and a radar dish turning on a mast at the back.
 */
export function Silo({ accent, glow }: ModelProps) {
  return (
    <>
      <mesh geometry={G.pad} material={CONCRETE} position={[0, 0.02, 0]} />
      <group position={[0, 0.04, SHAFT_Z]}>
        <mesh geometry={G.collar} material={DARK_CONCRETE} position={[0, 0.025, 0]} />
        <mesh geometry={G.shaft} material={TRACK} position={[0, 0.027, 0]} />
        <mesh geometry={G.missileBody} material={WHITE} position={[0, 0.1, 0]} />
        <mesh geometry={G.missileNose} material={accent} position={[0, 0.27, 0]} />
        {[-1, 1].map((side) => (
          <group
            key={side}
            position={[side * DOOR_HINGE, 0.05, 0]}
            rotation={[0, 0, -side * DOOR_OPEN]}
          >
            <mesh geometry={G.door} material={DARK_CONCRETE} position={[-side * 0.1, 0, 0]} />
            <mesh geometry={G.doorStripe} material={HAZARD} position={[-side * 0.17, 0.017, 0]} />
          </group>
        ))}
      </group>
      <group position={[-0.32, 0.04, 0.3]} rotation={[0, 0.5, 0]}>
        <mesh geometry={G.bunker} material={DARK_CONCRETE} position={[0, 0.05, 0]} />
        <mesh geometry={G.bunkerDoor} material={accent} position={[0, 0.035, 0.076]} />
        <mesh geometry={G.lamp} material={glow} position={[0.07, 0.112, 0]} />
      </group>
      <group position={RADAR}>
        <Pole length={MAST} position={[0, 0.04 + MAST / 2, 0]} material={STEEL} />
        <Spin axis="y" speed={RADAR_SPEED} position={[0, 0.04 + MAST, 0]}>
          <group rotation={[-0.4, 0, 0]} position={[0, 0.04, 0]}>
            <mesh geometry={G.dish} material={DISH} position={[0, 0, 0.1]} />
            {/* The feed horn, out from the bowl's centre. */}
            <Beam from={[0, 0, -0.01]} to={[0, 0, 0.1]} material={GUNMETAL} thickness={0.01} />
          </group>
        </Spin>
      </group>
    </>
  );
}
